import React from "react";
import LegalPage from "./LegalPage";

const H2 = ({ children }) => (
  <h2 className="font-condensed font-bold text-white text-2xl mt-10 mb-3">
    {children}
  </h2>
);

export default function TermsPage() {
  return (
    <LegalPage
      testId="terms-page"
      title="Terms & Conditions"
      updated="February 15, 2026"
      canonicalPath="/terms"
      description="Terms and Conditions governing use of Gadget Guy Media's website and services, including custom AI app builds and AI coaching."
    >
      <p>
        These Terms &amp; Conditions (&ldquo;<strong>Terms</strong>&rdquo;) govern your access to and use of{" "}
        <strong>gadgetguymedia.com</strong> (the &ldquo;Site&rdquo;) and any services provided by Gadget Guy Media LLC
        (&ldquo;<strong>Gadget Guy Media</strong>,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;). By using the Site or engaging our services, you
        agree to be bound by these Terms.
      </p>

      <H2>1. Services</H2>
      <p>Gadget Guy Media offers two primary services:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Custom AI App Builds</strong> — design, development, and deployment of custom AI agents,
          full-stack web applications, and automation systems, priced per project.
        </li>
        <li>
          <strong>AI Coaching</strong> — 1-on-1 instruction in prompting, vibe coding, AI agent design, and
          shipping products with AI, billed at <strong>$75 per hour</strong> with 30-minute increments.
        </li>
      </ul>
      <p>
        Specific scope, deliverables, and timelines are agreed to in writing (typically via a Statement of
        Work, invoice, or scheduling confirmation) before any work begins.
      </p>

      <H2>2. No-Cost Consultation</H2>
      <p>
        The initial consultation is complimentary and carries no obligation. It does not create a
        client relationship. A client relationship is formed only once both parties confirm scope and
        payment terms in writing.
      </p>

      <H2>3. Payment Terms</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Builds</strong> — invoices are due upon receipt unless otherwise agreed. Larger projects
          may be split into milestones, each with its own invoice.
        </li>
        <li>
          <strong>Coaching</strong> — sessions are pre-paid. Cancellations with less than 24 hours&apos; notice may
          be billed at 50% of the session rate.
        </li>
        <li>
          <strong>Late payments</strong> — invoices unpaid more than 15 days past due may incur a 1.5% monthly
          service charge and may pause active work.
        </li>
      </ul>

      <H2>4. Refunds</H2>
      <p>
        Because our work is custom and delivered incrementally, all payments are non-refundable once work has
        begun on a milestone. If you&apos;re unhappy with a deliverable, contact us and we&apos;ll work in good faith
        to resolve it.
      </p>

      <H2>5. Intellectual Property</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Client-owned</strong> — Upon full payment, you own the final custom code, designs, and
          content we deliver specifically for you.
        </li>
        <li>
          <strong>Gadget Guy Media-owned</strong> — We retain ownership of our pre-existing tools, templates,
          libraries, methodologies, and know-how used in creating your deliverables. You receive a
          non-exclusive, perpetual license to use these as embedded in your project.
        </li>
        <li>
          <strong>Third-party components</strong> — Some deliverables incorporate open-source or licensed
          third-party components governed by their own terms.
        </li>
        <li>
          <strong>Portfolio use</strong> — Unless we agree otherwise in writing, we may reference our work
          for you in our portfolio and marketing (name, logo, description, screenshots) after launch.
        </li>
      </ul>

      <H2>6. Client Responsibilities</H2>
      <p>You agree to:</p>
      <ul className="list-disc pl-6 space-y-2">
        <li>Provide accurate information, content, and assets in a timely manner.</li>
        <li>Respond to questions and approvals within a reasonable timeframe.</li>
        <li>Only send us material you have the right to use.</li>
      </ul>
      <p>
        Delays caused by lack of client response may extend the timeline and are not the responsibility of
        Gadget Guy Media.
      </p>

      <H2>7. Confidentiality</H2>
      <p>
        Both parties agree to treat non-public information shared during an engagement as confidential and to
        use it only for the purpose of delivering the services.
      </p>

      <H2>8. Warranty Disclaimer</H2>
      <p>
        The Site and services are provided <strong>&ldquo;as is&rdquo;</strong> and <strong>&ldquo;as available&rdquo;</strong> without warranties of any kind, either express or
        implied, including implied warranties of merchantability, fitness for a particular purpose, or
        non-infringement. We do not guarantee that any specific business result, ranking, revenue, or
        outcome will occur from the use of our services.
      </p>

      <H2>9. Limitation of Liability</H2>
      <p>
        To the fullest extent permitted by law, Gadget Guy Media&apos;s total liability arising out of or in
        connection with these Terms or your use of the Site or services shall not exceed the total amount
        paid by you to Gadget Guy Media in the twelve (12) months preceding the claim. We are not liable for
        any indirect, incidental, special, consequential, or punitive damages.
      </p>

      <H2>10. Third-Party Products</H2>
      <p>
        Our services may integrate with third-party platforms (e.g., OpenAI, Anthropic, Stripe, SendGrid,
        MongoDB). We are not responsible for third-party outages, policy changes, or pricing changes that
        affect your project.
      </p>

      <H2>11. Termination</H2>
      <p>
        Either party may terminate an engagement with written notice. Upon termination, you agree to pay for
        all work completed up to the termination date. Provisions relating to IP, confidentiality, and
        liability survive termination.
      </p>

      <H2>12. Governing Law</H2>
      <p>
        These Terms are governed by the laws of the <strong>State of Tennessee</strong>, USA, without regard
        to conflict-of-law principles. Any dispute shall be resolved in the state or federal courts located
        in Sumner County, Tennessee.
      </p>

      <H2>13. Changes to These Terms</H2>
      <p>
        We may update these Terms from time to time. Continued use of the Site or services after changes
        constitutes acceptance of the revised Terms.
      </p>

      <H2>14. Contact</H2>
      <p>
        Questions about these Terms? Reach out at{" "}
        <a href="mailto:info@gadgetguymedia.com" className="text-[#28C8F0] hover:underline">
          info@gadgetguymedia.com
        </a>
        .
      </p>
    </LegalPage>
  );
}
